/**
 * Form schema (AGENTS.md 6.2, 6.3). The form is data; the chat UI is one
 * renderer of it. Published versions are immutable.
 */
import { z } from "zod";
import { idSchema, localeSchema, urlSchema } from "../common.js";
export const NODE_TYPES = [
    "message",
    "consent",
    "text",
    "long_text",
    "email",
    "phone",
    "number",
    "date",
    "single_choice",
    "multi_choice",
    "procedure_picker",
    "file_upload",
    "booking", // Phase 2
    "payment", // Phase 2
];
export const nodeTypeSchema = z.enum(NODE_TYPES);
/** Node types that collect an answer (everything except informational messages). */
export const ANSWERING_NODE_TYPES = NODE_TYPES.filter((t) => t !== "message");
export const DEFAULT_TEXT_MAX = 300;
export const DEFAULT_LONG_TEXT_MAX = 800;
// ---- showIf (6.4) ----
const leafCondition = z.discriminatedUnion("op", [
    z.object({
        op: z.literal("equals"),
        node: idSchema,
        value: z.union([z.string(), z.number(), z.boolean()]),
    }),
    z.object({
        op: z.literal("notEquals"),
        node: idSchema,
        value: z.union([z.string(), z.number(), z.boolean()]),
    }),
    z.object({ op: z.literal("includes"), node: idSchema, value: z.string() }),
    z.object({ op: z.literal("answered"), node: idSchema }),
]);
export const conditionSchema = z.lazy(() => z.union([
    leafCondition,
    z.object({ all: z.array(conditionSchema).min(1) }),
    z.object({ any: z.array(conditionSchema).min(1) }),
]));
// ---- shared node fields ----
export const messageOptionsSchema = z.object({
    include: z.boolean().default(true),
    label: z.string().max(60).optional(),
});
export const nodeActionSchema = z.enum(["whatsapp", "booking"]);
const baseNode = z.object({
    id: idSchema,
    prompt: z.string().max(500).optional(),
    help: z.string().max(2000).optional(),
    required: z.boolean().default(false),
    showIf: conditionSchema.optional(),
    /** Canonical lead field this answer maps to, e.g. "contact.email". */
    maps: z
        .enum([
        "contact.fullName",
        "contact.firstName",
        "contact.lastName",
        "contact.email",
        "contact.phone",
        "contact.dateOfBirth",
        "contact.age",
        "contact.gender",
        "contact.location",
        "contact.country",
        "contact.city",
    ])
        .optional(),
    message: messageOptionsSchema.optional(),
    actions: z.array(nodeActionSchema).optional(),
});
export const choiceOptionSchema = z.object({ id: idSchema, label: z.string().min(1).max(120) });
export const consentItemSchema = z.object({
    key: z.enum(["data_processing", "marketing"]),
    required: z.boolean(),
    text: z.string().min(1).max(1000),
});
export const nodeSchema = z.discriminatedUnion("type", [
    baseNode.extend({
        type: z.literal("message"),
        text: z.string().min(1).max(2000),
        imagePath: z.string().max(512).optional(),
    }),
    baseNode.extend({
        type: z.literal("consent"),
        items: z.array(consentItemSchema).min(1).max(5),
        policyUrl: urlSchema.optional(),
        /** Version label of the consent wording, stored on the lead (section 12). */
        textVersion: z.string().max(40).default("1"),
    }),
    baseNode.extend({
        type: z.literal("text"),
        maxLength: z.number().int().min(1).max(DEFAULT_TEXT_MAX).default(DEFAULT_TEXT_MAX),
    }),
    baseNode.extend({
        type: z.literal("long_text"),
        maxLength: z.number().int().min(1).max(DEFAULT_LONG_TEXT_MAX).default(DEFAULT_LONG_TEXT_MAX),
    }),
    baseNode.extend({ type: z.literal("email") }),
    baseNode.extend({ type: z.literal("phone"), defaultCountry: z.string().length(2).default("GB") }),
    baseNode.extend({
        type: z.literal("number"),
        min: z.number().optional(),
        max: z.number().optional(),
    }),
    baseNode.extend({
        type: z.literal("date"),
        minDate: z.string().optional(),
        maxDate: z.string().optional(),
    }),
    baseNode.extend({
        type: z.literal("single_choice"),
        options: z.array(choiceOptionSchema).min(1).max(30),
        allowOther: z.boolean().default(false),
    }),
    baseNode.extend({
        type: z.literal("multi_choice"),
        options: z.array(choiceOptionSchema).min(1).max(30),
        allowOther: z.boolean().default(false),
        maxSelections: z.number().int().min(1).optional(),
    }),
    baseNode.extend({
        type: z.literal("procedure_picker"),
        multiple: z.boolean().default(true),
        categories: z.array(z.string()).optional(),
        /**
         * Ask for the main heading (category) first, then show only the procedures under it
         * (owner request, 14 October 2026). A heading whose only procedure has the same name
         * (for example "Mole mapping") is chosen directly.
         */
        groupFirst: z.boolean().optional(),
    }),
    baseNode.extend({
        type: z.literal("file_upload"),
        accept: z.array(z.string()).default(["image/*", "application/pdf"]),
        maxFiles: z.number().int().min(1).max(10).default(5),
        maxSizeMb: z.number().int().min(1).max(25).default(15),
    }),
    baseNode.extend({ type: z.literal("booking") }),
    baseNode.extend({ type: z.literal("payment") }),
]);
export const stepSchema = z.object({
    id: idSchema,
    title: z.string().min(1).max(80),
    /**
     * "chat": one question at a time. "card" (14b): the step's questions are shown
     * together as standard fields with one "Done, let's continue" button; the
     * answers appear as a single patient bubble. Message, consent, file and
     * procedure nodes in a card step are still shown one at a time.
     */
    presentation: z.enum(["chat", "card"]).optional(),
    nodes: z.array(nodeSchema).min(1).max(50),
});
export const formSchemaV1 = z
    .object({
    schemaVersion: z.literal(1),
    locale: localeSchema,
    steps: z.array(stepSchema).min(1).max(30),
    end: z.object({ actions: z.array(nodeActionSchema).min(1) }),
})
    .superRefine((form, ctx) => {
    const seen = new Set();
    let firstCollecting;
    let consentIndex = -1;
    let i = 0;
    for (const step of form.steps) {
        for (const node of step.nodes) {
            if (seen.has(node.id))
                ctx.addIssue({ code: "custom", message: `duplicate node id "${node.id}"` });
            seen.add(node.id);
            if (node.type === "consent" && consentIndex < 0)
                consentIndex = i;
            if (node.type !== "message" && node.type !== "consent" && firstCollecting === undefined)
                firstCollecting = node.id;
            if (node.type !== "message" && node.type !== "consent" && consentIndex < 0) {
                ctx.addIssue({
                    code: "custom",
                    message: `node "${node.id}" collects data before the consent node (section 12)`,
                });
                return;
            }
            i++;
        }
    }
    if (consentIndex < 0)
        ctx.addIssue({ code: "custom", message: "a consent node is required (section 12)" });
});
/** Validates a form for publishing: consent first, unique ids, resolvable showIf references. */
export function validateFormForPublish(form) {
    const parsed = formSchemaV1.safeParse(form);
    if (!parsed.success)
        return { ok: false, issues: parsed.error.issues.map((e) => e.message) };
    const ids = new Set(parsed.data.steps.flatMap((s) => s.nodes.map((n) => n.id)));
    const issues = [];
    const check = (c, owner) => {
        if ("all" in c)
            c.all.forEach((x) => check(x, owner));
        else if ("any" in c)
            c.any.forEach((x) => check(x, owner));
        else if (!ids.has(c.node))
            issues.push(`node "${owner}" has a showIf referring to unknown node "${c.node}"`);
    };
    for (const s of parsed.data.steps)
        for (const n of s.nodes)
            if (n.showIf)
                check(n.showIf, n.id);
    return issues.length ? { ok: false, issues } : { ok: true, form: parsed.data };
}
//# sourceMappingURL=index.js.map